"use client";

import { MedicalCheckupIcon } from "@/components/icons/MedicalCheckupIcon";
import { Badge } from "@/components/ui/badge";
import { getMembersZendeskUrl } from "@/constants/zendesk-urls/members-zendesk-urls";
import { PagesPathname } from "@/dictionaries/pageTitle/types";
import { isFeatureFlagStressCheckEnabled } from "@/env/config";
import { cn } from "@/lib/utils";
import { membersZendeskSsoLink } from "@/server/models/zendesk/zendesk-sso-link";
import { pagesPath } from "@/utils/pages-path";
import FeedRectangleIcon from "@public/icons/feed-rectangle-icon.svg";
import HelpIcon from "@public/icons/help-icon.svg";
import HomeIcon from "@public/icons/home-icon.svg";
import MenuIcon from "@public/icons/menu-icon.svg";
import MyNumberCardIcon from "@public/icons/my-number-card-icon.svg";
import OffersIcon from "@public/icons/offers-icon.svg";
import Link from "next/link";
import { usePathname } from "next/navigation";
import {
  type ComponentType,
  type FC,
  type ReactNode,
  useCallback,
} from "react";
import { MdOutlinePsychology } from "react-icons/md";

// Material のアイコンは viewBox に余白があるため、他のサイドメニューアイコン
// （16〜18px の SVG）と見た目の大きさを揃えるには一回り大きく指定する
const StressCheckIcon = ({ className }: { className?: string }) => (
  <MdOutlinePsychology className={cn("size-6", className)} />
);

const NavMenuItem = ({
  content,
  href,
  Icon,
  active,
  isExternal = false,
}: {
  content: ReactNode;
  href: string;
  Icon: ComponentType<{ className?: string }>;
  active?: boolean;
  isExternal?: boolean;
}) => {
  return (
    <Link
      href={href}
      target={isExternal ? "_blank" : undefined}
      className={cn(
        "group",
        "flex h-12 w-full flex-row items-center gap-2 px-6 font-bold transition",
        "hover:bg-primary-0.5",
        active && "bg-primary-0.5",
      )}
      prefetch={!isExternal}
    >
      <span className="inline-flex w-6 items-center justify-center">
        <Icon
          className={cn(
            active
              ? "fill-primary-7 text-primary-7"
              : "fill-monotone-7 text-monotone-7",
            "group-hover:fill-primary-7 group-hover:text-primary-7",
          )}
        />
      </span>
      {content}
    </Link>
  );
};

const useIsActiveMenuItem = () => {
  const pathname = usePathname();
  return useCallback(
    (tabPathname: PagesPathname) => {
      // ホームのパス "/" の場合 split("/")[1] が空文字を返すため、undefinedに変換してtabPathと一致させる
      const navPath = pathname.split("/")[1] || undefined;
      const tabPath = tabPathname.split("/")[2];
      return navPath === tabPath;
    },
    [pathname],
  );
};

export const MembersSideMenuNav: FC<{
  actionRequiredApplicationsCount: number;
}> = ({ actionRequiredApplicationsCount }) => {
  const isActive = useIsActiveMenuItem();
  const memberPagesPath = pagesPath.members;

  return (
    <nav className="flex flex-col">
      <NavMenuItem
        content="ホーム"
        href={memberPagesPath.$url().path}
        Icon={HomeIcon}
        active={isActive(memberPagesPath.$url().pathname)}
      />
      <NavMenuItem
        content="お知らせ"
        href={memberPagesPath.news.$url().path}
        Icon={FeedRectangleIcon}
        active={isActive(memberPagesPath.news.$url().pathname)}
      />
      <NavMenuItem
        content={
          <span className="inline-flex w-full items-center justify-between">
            <span>各種届出</span>
            {actionRequiredApplicationsCount > 0 && (
              <Badge size="sm" className="hover:opacity-100">
                {actionRequiredApplicationsCount}
              </Badge>
            )}
          </span>
        }
        href={memberPagesPath.applications.$url().path}
        Icon={MenuIcon}
        active={isActive(memberPagesPath.applications.$url().pathname)}
      />
      <NavMenuItem
        content="特典サービス"
        href={memberPagesPath.offers.$url().path}
        Icon={OffersIcon}
        active={isActive(memberPagesPath.offers.$url().pathname)}
      />
      <NavMenuItem
        content="健康診断"
        href={memberPagesPath.medical_checkups.$url().path}
        Icon={MedicalCheckupIcon}
        active={isActive(memberPagesPath.medical_checkups.$url().pathname)}
      />
      {/* ストレスチェックは受検した加入者だけが使う画面のため、機能が有効な環境でのみ出す */}
      {isFeatureFlagStressCheckEnabled() && (
        <NavMenuItem
          content="ストレスチェック"
          href={memberPagesPath.stress_check_results.$url().path}
          Icon={StressCheckIcon}
          active={isActive(
            memberPagesPath.stress_check_results.$url().pathname,
          )}
        />
      )}
      <NavMenuItem
        content="保険資格"
        href={memberPagesPath.insurance_card.$url().path}
        Icon={MyNumberCardIcon}
        active={isActive(memberPagesPath.insurance_card.$url().pathname)}
      />
      <NavMenuItem
        content="ヘルプ"
        href={membersZendeskSsoLink(getMembersZendeskUrl("ヘルプ"))}
        Icon={HelpIcon}
        isExternal
      />
    </nav>
  );
};
